import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { getMe, getIsLoggedIn, getJWTToken } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import cookies from '@utils/cookies';
import { localStorageAvailable } from '@utils/localStorage';
import {
  getPlusSubscriptionsUids,
  getActiveOfflineSubscriptionUIDs,
  getLiteSubscriptionsUids
} from '@models/Me';
import { useIsMobile } from '@stores/AppCommonData';
import useConstants from '@api/hooks/useConstants';
import { isUAIconsEnabled } from '@api/selectors/constants';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import useExperiments from '@stores/experiments';
import { DEVICE_ID_COOKIE_NAME } from '@utils/session/validateAndSetSessionId';
import { abVariations } from '@utils/abHelper';
import { DEFAULT_STORE_TAB } from '../constants';

const useGoalPageRedirection = ({
  goalUID,
  goalSlug,
  querySlug,
  storeTabWhiteListedGoalUid
}) => {
  const { goalInfo } = useGoalInfo(goalUID);
  const { isPlusAvailable, isRecordedGoal, isUaIconGoal } = goalInfo;
  const router = useRouter();
  const meInfo = useMyInfo(getMe);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isMobile = useIsMobile();
  const { constants } = useConstants();
  const isIconsEnabled = isUAIconsEnabled(constants);

  const { isSubscribed, isSubscribedToLite } = useMemo(() => {
    const plusSubscriptions = getPlusSubscriptionsUids(meInfo);
    const liteSubscriptions = getLiteSubscriptionsUids(meInfo);
    const offlineSubscriptions = getActiveOfflineSubscriptionUIDs(meInfo);
    const isSubscribedUser = [
      ...plusSubscriptions,
      ...offlineSubscriptions
    ].includes(goalUID);
    const isSubscribedLiteUser = liteSubscriptions?.includes(goalUID);
    return {
      isSubscribed: isSubscribedUser,
      isSubscribedToLite: isSubscribedLiteUser
    };
  }, [meInfo, goalUID]);

  const meJwt = useMyInfo(getJWTToken);

  useEffect(() => {
    if (isLoggedIn) {
      const deviceID = cookies.readCookie(DEVICE_ID_COOKIE_NAME);
      fetchABExperimentData(DEFAULT_STORE_TAB, meJwt, deviceID);
    }
  }, [isLoggedIn, meJwt]);
  const referralExitIntent = useExperiments(
    getExperimentVariation(DEFAULT_STORE_TAB)
  );

  const isABTestingUser = referralExitIntent === abVariations.TEST_1;

  useEffect(() => {
    const parsedGoalUid = storeTabWhiteListedGoalUid?.description
      ? JSON.parse(storeTabWhiteListedGoalUid.description).goal_uid
      : null;
    const isStoreRedirect = parsedGoalUid
      ? parsedGoalUid.indexOf(goalUID) !== -1
      : false;
    if (isLoggedIn) {
      if (isSubscribed && isPlusAvailable) {
        let redirectionURL = `/goal/${goalSlug}/${goalUID}/planner`;
        if (isUaIconGoal) {
          if (isSubscribed) redirectionURL = '/icons';
          else redirectionURL = `/goal/${goalSlug}/${goalUID}/icons-home`;
        } else if (isRecordedGoal) {
          redirectionURL = `/goal/${goalSlug}/${goalUID}/browse`;
        }
        router.push(redirectionURL);
      } else if (isSubscribedToLite) {
        router.push(`/goal/${goalSlug}/${goalUID}/browse`);
      } else if (
        !isSubscribed &&
        isStoreRedirect &&
        isABTestingUser &&
        localStorageAvailable
      ) {
        let storedGoalUIDS = localStorage.getItem('storeRedirectUid');
        if (storedGoalUIDS) {
          const goalArr = storedGoalUIDS.split(',');
          const goalExist = goalArr.indexOf(goalUID);
          if (goalExist === -1) {
            storedGoalUIDS = `${storedGoalUIDS},${goalUID}`;
            localStorage.setItem('storeRedirectUid', storedGoalUIDS);
            router.push(`/goal/${goalSlug}/${goalUID}/store`);
          }
        } else {
          localStorage.setItem('storeRedirectUid', goalUID);
          router.push(`/goal/${goalSlug}/${goalUID}/store`);
        }
      }
    } else if (isUaIconGoal && !!constants && !isIconsEnabled) {
      router.push('/explore');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    goalUID,
    isSubscribed,
    isSubscribedToLite,
    router,
    constants,
    isPlusAvailable,
    isLoggedIn,
    isUaIconGoal,
    isIconsEnabled,
    isRecordedGoal,
    isMobile,
    isABTestingUser
  ]);

  useEffect(() => {
    // Slug Correction
    if (querySlug !== goalSlug)
      router.replace(
        '/goal/[goalSlug]/[goalUID]',
        `/goal/${goalSlug}/${goalUID}/`,
        { shallow: true }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);
};

export default useGoalPageRedirection;
